import { useEffect, useRef } from 'react';

import { expVal } from '@confluence/feature-experiments';

import { ConnectStaticMacroSSRExperienceTrackerSupport } from './ConnectStaticMacroSSRExperienceTrackerSupport.ts';

type ConnectStaticMacroSSRExperienceTrackingData = {
	contentId?: string;
	extensionKey?: string;
	isActuallyMissingMacroParameters?: boolean;
	isConnectMacro?: boolean;
	macroHtml?: string | null;
	macroId?: string;
};

export const useConnectStaticMacroSSRExperienceTracking = ({
	contentId,
	extensionKey,
	isActuallyMissingMacroParameters,
	isConnectMacro,
	macroHtml,
	macroId,
}: ConnectStaticMacroSSRExperienceTrackingData) => {
	const connectStaticMacroSSRExperienceStarted = useRef(false);
	const connectStaticMacroSSRExperienceResolved = useRef(false);

	useEffect(() => {
		if (!expVal('confluence_frontend_macro_slo_metrics', 'isEnabled', false)) {
			return undefined;
		}

		const connectStaticMacroSSRData = {
			contentId,
			extensionKey,
			macroId,
			isConnectMacro,
		};

		let shouldTrackConnectStaticMacroSSR = false;
		try {
			shouldTrackConnectStaticMacroSSR =
				ConnectStaticMacroSSRExperienceTrackerSupport.isTrackable(connectStaticMacroSSRData);
		} catch {
			shouldTrackConnectStaticMacroSSR = false;
		}

		if (!shouldTrackConnectStaticMacroSSR) {
			return undefined;
		}

		if (!connectStaticMacroSSRExperienceStarted.current) {
			try {
				ConnectStaticMacroSSRExperienceTrackerSupport.start(connectStaticMacroSSRData);
			} catch {
				// Telemetry must never block or alter macro rendering behaviour.
			} finally {
				connectStaticMacroSSRExperienceStarted.current = true;
			}
		}

		if (connectStaticMacroSSRExperienceResolved.current) {
			return undefined;
		}

		if (macroHtml && !isActuallyMissingMacroParameters) {
			try {
				ConnectStaticMacroSSRExperienceTrackerSupport.succeed();
			} catch {
				// Telemetry must never block or alter macro rendering behaviour.
			} finally {
				connectStaticMacroSSRExperienceResolved.current = true;
			}
		} else if (isActuallyMissingMacroParameters) {
			try {
				ConnectStaticMacroSSRExperienceTrackerSupport.fail(
					new Error(
						'Connect static macro SSR render failed because required macro parameters are missing',
					),
				);
			} catch {
				// Telemetry must never block or alter macro rendering behaviour.
			} finally {
				connectStaticMacroSSRExperienceResolved.current = true;
			}
		}

		return () => {
			if (
				connectStaticMacroSSRExperienceStarted.current &&
				!connectStaticMacroSSRExperienceResolved.current
			) {
				try {
					ConnectStaticMacroSSRExperienceTrackerSupport.abort(
						'FabricLegacyMacrosServerRenderer unmounted before Connect static macro SSR experience resolved',
					);
				} catch {
					// Telemetry must never block or alter macro rendering behaviour.
				}
			}
		};
	}, [
		contentId,
		extensionKey,
		isActuallyMissingMacroParameters,
		isConnectMacro,
		macroHtml,
		macroId,
	]);
};
